import { useEffect, useMemo, useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import { ChevronRight, Plus, Minus } from 'lucide-react'
import { cn } from '../../utils/cn'
import type { TradingAttention } from '../../lib/attention/tradingAttention'
import {
  NAV_TREE, isNode, pathOf, collectGroupIds, descendantCount, anyVisible,
  type NavNode, type Leaf,
} from '../../lib/nav/navTree'

// The workflow-grouped accordion nav body (lives inside the real Sidebar chrome).
// Strict one-open-at-a-time for top sections; global +/− overrides; per-child
// attention badges with parent rollups; parent = link + toggle (Finance pattern).
// Re-homing only — every route + permission key comes verbatim from NAV_TREE.

const OPEN_KEY = 'sidebar.nav.open.v1'

type Props = {
  collapsed?: boolean
  onClose: () => void
  // Page-level visibility (mirrors the old Sidebar's pageVisible).
  canSee: (pageKey: string) => boolean
  // Business Settings stays owner/admin-only (matches the old rail gate).
  canManageBusinessSettings: boolean
  attention: TradingAttention
}

export function AccordionNav({ collapsed, onClose, canSee, canManageBusinessSettings, attention }: Props) {
  const { pathname } = useLocation()

  // Real per-child badge counts: match a leaf route against the attention model's
  // leaf links (query-insensitive). Parents roll up = Σ descendants.
  const countFor = useMemo(() => {
    const byPath = new Map<string, number>()
    for (const l of attention.leaves) {
      const p = pathOf(l.link)
      byPath.set(p, (byPath.get(p) ?? 0) + l.count)
    }
    return (to: string) => byPath.get(pathOf(to)) ?? 0
  }, [attention.leaves])

  const allGroupIds = useMemo(() => collectGroupIds(NAV_TREE), [])

  // Which top section does the current path belong to?
  const currentTopId = useMemo(() => {
    const top = NAV_TREE.find(n => isNode(n) && n.to && pathname.startsWith(pathOf(n.to))) as NavNode | undefined
    return top?.label ?? null
  }, [pathname])

  const [expanded, setExpanded] = useState<Set<string>>(() => {
    try {
      const raw = localStorage.getItem(OPEN_KEY)
      if (raw) return new Set<string>(JSON.parse(raw))
    } catch { /* ignore */ }
    return new Set<string>(currentTopId ? [currentTopId] : ['Trading'])
  })

  useEffect(() => {
    try { localStorage.setItem(OPEN_KEY, JSON.stringify([...expanded])) } catch { /* ignore */ }
  }, [expanded])

  // Auto-open the section you're in — but only when arriving from another section,
  // so within-section navigation keeps whatever nested groups you had open.
  useEffect(() => {
    if (currentTopId && !expanded.has(currentTopId)) {
      setExpanded(new Set([currentTopId]))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTopId])

  const toggle = (id: string, isTop: boolean) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        for (const k of [...next]) if (k === id || k.startsWith(id + '/')) next.delete(k)
      } else {
        if (isTop) next.clear() // strict one-open-at-a-time for top sections
        next.add(id)
      }
      return next
    })
  }
  const expandAll = () => setExpanded(new Set(allGroupIds))
  const collapseAll = () => setExpanded(new Set())

  // Top-level visibility (matches the old rail: Dashboard/Help special-cased,
  // Business Settings owner/admin-only).
  const topVisible = (n: NavNode | Leaf): boolean => {
    if (!isNode(n)) return n.pkey ? canSee(n.pkey) : true
    if (n.label === 'Business Settings') return canManageBusinessSettings && anyVisible(n, canSee)
    return anyVisible(n, canSee)
  }
  const tops = NAV_TREE.filter(topVisible)

  // ── Collapsed icon rail: top sections only, click → hub (no accordion) ──
  if (collapsed) {
    return (
      <div className="space-y-0.5">
        {tops.map(n => {
          const to = isNode(n) ? (n.to ?? '#') : n.to
          const rollup = isNode(n) ? descendantCount(n, countFor) : (n.to ? countFor(n.to) : 0)
          const active = pathname === pathOf(to) || (isNode(n) && !!n.to && pathname.startsWith(pathOf(n.to)))
          return (
            <NavLink
              key={n.label}
              to={to}
              onClick={onClose}
              title={n.label}
              className={cn(
                'relative flex items-center justify-center px-2 py-2.5 rounded-xl transition-colors',
                active ? 'text-brand-600 bg-brand-50' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900',
              )}
            >
              <span className="text-[18px] leading-none">{isNode(n) ? n.icon : n.icon}</span>
              {rollup > 0 && <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-red-500 ring-2 ring-white" />}
            </NavLink>
          )
        })}
      </div>
    )
  }

  // ── Full accordion ──
  return (
    <div>
      <div className="flex items-center justify-end gap-1 px-1 pb-1">
        <button type="button" onClick={expandAll} title="Expand all"
          className="h-6 w-6 grid place-items-center rounded-md text-gray-400 hover:bg-brand-50 hover:text-brand-600">
          <Plus className="h-3.5 w-3.5" />
        </button>
        <button type="button" onClick={collapseAll} title="Collapse all"
          className="h-6 w-6 grid place-items-center rounded-md text-gray-400 hover:bg-brand-50 hover:text-brand-600">
          <Minus className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="space-y-0.5">
        {tops.map(n => (
          <Row key={n.label} node={n} id={n.label} depth={0} isTop
               expanded={expanded} toggle={toggle} countFor={countFor}
               canSee={canSee} pathname={pathname} onClose={onClose} />
        ))}
      </div>
    </div>
  )
}

function Row({
  node, id, depth, isTop, expanded, toggle, countFor, canSee, pathname, onClose,
}: {
  node: NavNode | Leaf
  id: string
  depth: number
  isTop?: boolean
  expanded: Set<string>
  toggle: (id: string, isTop: boolean) => void
  countFor: (to: string) => number
  canSee: (k: string) => boolean
  pathname: string
  onClose: () => void
}) {
  if (!anyVisible(node, canSee)) return null
  const pad = { paddingLeft: `${10 + depth * 12}px` }

  if (!isNode(node)) {
    const active = pathname === pathOf(node.to)
    const count = node.to ? countFor(node.to) : 0
    return (
      <Link
        to={node.to}
        onClick={onClose}
        style={pad}
        className={cn(
          'relative flex items-center gap-2 pr-2.5 py-2 rounded-lg text-sm transition-colors',
          active
            ? 'bg-brand-50 text-brand-700 font-medium border-l-2 border-brand-500'
            : 'text-gray-600 hover:bg-brand-50 hover:text-brand-600',
        )}
      >
        <span className="flex-1 truncate">{node.label}</span>
        {count > 0 && (
          <span className="ml-auto text-white text-[11px] bg-red-500 rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{count}</span>
        )}
      </Link>
    )
  }

  const open = expanded.has(id)
  const rollup = descendantCount(node, countFor)

  return (
    <div>
      <div
        style={pad}
        className={cn(
          'group flex items-center gap-2 pr-1 py-2 rounded-lg text-sm transition-colors',
          open ? 'text-gray-900 font-medium' : 'text-gray-700 hover:bg-brand-50 hover:text-brand-600',
        )}
      >
        {node.to ? (
          <Link to={node.to} onClick={onClose} className="flex items-center gap-2.5 flex-1 min-w-0">
            {node.icon && <span className="text-[18px] leading-none flex-shrink-0">{node.icon}</span>}
            <span className="truncate">{node.label}</span>
          </Link>
        ) : (
          <button type="button" onClick={() => toggle(id, !!isTop)} className="flex items-center gap-2.5 flex-1 min-w-0 text-left">
            {node.icon && <span className="text-[18px] leading-none flex-shrink-0">{node.icon}</span>}
            <span className="truncate">{node.label}</span>
          </button>
        )}

        {rollup > 0 && !open && (
          <span className="text-white text-[11px] bg-red-500 rounded-full px-1.5 py-0.5 min-w-[18px] text-center">{rollup}</span>
        )}

        <button
          type="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); toggle(id, !!isTop) }}
          aria-label={open ? `Collapse ${node.label}` : `Expand ${node.label}`}
          className="flex-shrink-0 h-6 w-6 grid place-items-center rounded-md text-gray-400 hover:bg-brand-100 hover:text-brand-600"
        >
          <ChevronRight className={cn('h-4 w-4 transition-transform duration-200', open && 'rotate-90')} />
        </button>
      </div>

      {open && (
        <div className="mt-0.5 space-y-0.5">
          {node.children.map(child => (
            <Row key={child.label} node={child} id={id + '/' + child.label} depth={depth + 1}
                 expanded={expanded} toggle={toggle} countFor={countFor}
                 canSee={canSee} pathname={pathname} onClose={onClose} />
          ))}
        </div>
      )}
    </div>
  )
}
